import axiosInstance from '../../api/axios';
import { scopeParams, type AnalyticsScope } from './scope.ts';

// Row types mirror the backend row structs (and their `json` tags) verbatim —
// see skeptics-backend/repository/analytics_reports_repository.go (CFO
// section, reports #1-#8). Do not rename fields without updating both sides
// of the contract.
//
// Envelope shapes (from analytics_reports_handlers.go, verbatim):
//   #1 compliance-score -> { row: CFOComplianceScoreRow, not_computed: bool }
//      (a single object, NOT a rows array — the handler always returns
//      exactly one row, either the persisted `report_compliance_score`
//      row or an on-the-fly computed one flagged `not_computed: true`
//      when the nightly refresh hasn't run yet.)
//   #3 open-issues       -> { summary: CFOOpenIssuesRow, by_department: CFOOpenIssuesByDeptRow[] }
//   everything else      -> { rows: T[] }

/** Report #1 — SOX Compliance Score (Executive KPI). */
export type CFOComplianceScoreRow = {
  score: number;
  coverage_ratio: number | null;
  test_pass_ratio: number | null;
  issue_penalty: number | null;
  band: string;
  refreshed_at: string | null;
};

export type CFOComplianceScoreResponse = {
  row: CFOComplianceScoreRow;
  not_computed: boolean;
};

/** Report #2 — Control Coverage Heatmap. */
export type CFOCoverageHeatmapRow = {
  cycle: string;
  controls: number;
  active_controls: number;
};

/** Report #3's summary tiles. */
export type CFOOpenIssuesRow = {
  open_count: number;
  escalated_count: number;
  repeat_count: number;
  overdue_action_plans: number;
};

/** Report #3's department x classification breakdown bar chart series. */
export type CFOOpenIssuesByDeptRow = {
  department: string;
  classification: string;
  count: number;
};

export type CFOOpenIssuesResponse = {
  summary: CFOOpenIssuesRow;
  by_department: CFOOpenIssuesByDeptRow[];
};

/** Report #4 — Program Progress Summary (proxy). */
export type CFOProgramProgressRow = {
  result: string;
  controls: number;
};

/** Report #5 — SOC Report Coverage. */
export type CFOSOCCoverageRow = {
  system_code: string;
  name: string;
  key_system: boolean;
  year: number | null;
  soc1_type1: boolean | null;
  soc1_type2: boolean | null;
  soc2_type1: boolean | null;
  soc2_type2: boolean | null;
  bridge_letter: boolean | null;
};

/** Report #6 — Risk Landscape Summary. */
export type CFORiskLandscapeRow = {
  classification: string;
  risk_level: string | null;
  cnt: number;
};

/** Report #7 — Repeat & Escalated Issues. */
export type CFORepeatEscalatedRow = {
  id: string;
  title: string;
  status: string;
  vintage_count: number;
  is_escalated: boolean;
  department: string | null;
  period: string | null;
};

/** Report #8 — Compliance Score Trend (12-month). */
export type CFOScoreTrendRow = {
  snapshot_date: string;
  score: number;
  band: string;
};

type RowsEnvelope<T> = { rows: T[] };

async function getRows<T>(path: string, scope?: AnalyticsScope): Promise<T[]> {
  const response = await axiosInstance.get<RowsEnvelope<T>>(path, { params: scopeParams(scope) });
  return response?.data?.rows ?? [];
}

const BASE = '/api/analytics/cfo';

const DEFAULT_OPEN_ISSUES_SUMMARY: CFOOpenIssuesRow = {
  open_count: 0,
  escalated_count: 0,
  repeat_count: 0,
  overdue_action_plans: 0,
};

export const fetchCfoComplianceScore = async (scope?: AnalyticsScope): Promise<CFOComplianceScoreResponse | null> => {
  const response = await axiosInstance.get<CFOComplianceScoreResponse>(`${BASE}/compliance-score`, { params: scopeParams(scope) });
  return response?.data ?? null;
};

export const fetchCfoCoverageHeatmap = (scope?: AnalyticsScope): Promise<CFOCoverageHeatmapRow[]> =>
  getRows<CFOCoverageHeatmapRow>(`${BASE}/coverage-heatmap`, scope);

export const fetchCfoOpenIssues = async (scope?: AnalyticsScope): Promise<CFOOpenIssuesResponse> => {
  const response = await axiosInstance.get<CFOOpenIssuesResponse>(`${BASE}/open-issues`, { params: scopeParams(scope) });
  return {
    summary: response?.data?.summary ?? DEFAULT_OPEN_ISSUES_SUMMARY,
    by_department: response?.data?.by_department ?? [],
  };
};

export const fetchCfoProgramProgress = (scope?: AnalyticsScope): Promise<CFOProgramProgressRow[]> =>
  getRows<CFOProgramProgressRow>(`${BASE}/program-progress`, scope);

export const fetchCfoSOCCoverage = (scope?: AnalyticsScope): Promise<CFOSOCCoverageRow[]> =>
  getRows<CFOSOCCoverageRow>(`${BASE}/soc-coverage`, scope);

export const fetchCfoRiskLandscape = (scope?: AnalyticsScope): Promise<CFORiskLandscapeRow[]> =>
  getRows<CFORiskLandscapeRow>(`${BASE}/risk-landscape`, scope);

export const fetchCfoRepeatEscalated = (scope?: AnalyticsScope): Promise<CFORepeatEscalatedRow[]> =>
  getRows<CFORepeatEscalatedRow>(`${BASE}/repeat-escalated`, scope);

export const fetchCfoScoreTrend = (scope?: AnalyticsScope): Promise<CFOScoreTrendRow[]> =>
  getRows<CFOScoreTrendRow>(`${BASE}/score-trend`, scope);
